import { navigate } from 'gatsby';
import type { MUIDataTableOptions } from 'mui-datatables';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import RoundedButton from '@components/RoundedButton';

import * as ROUTES from '@constants/routes';

import { formatDate } from '@utils/format';

import { useAdminListPolicies } from 'generated/admin.generated';

const messages = defineMessages({
  name: {
    id: 'admin.policies.name',
    defaultMessage: 'Name',
  },
  description: {
    id: 'admin.policies.description',
    defaultMessage: 'Description',
  },
  updatedAt: {
    id: 'GLOBAL.updatedAt',
    defaultMessage: 'Updated at',
  },
  createdAt: {
    id: 'GLOBAL.createdAt',
    defaultMessage: 'Created At',
  },
  noData: {
    id: 'admin.policies.noData',
    defaultMessage: 'No policies',
  },
  create: {
    id: 'admin.policies.create',
    defaultMessage: 'New Policy',
  },
});

interface RowData {
  id: string;
  name?: string | null;
  description?: string | null;
  created_at: string;
  updated_at: string;
}

function AdminPoliciesList() {
  const { formatMessage } = useIntl();
  const { data, loading } = useAdminListPolicies();

  const tableData = React.useMemo(
    () =>
      (data?.listPolicies ?? []).map<RowData>((policy) => {
        return {
          id: policy.policy_id,
          name: policy.policy_name,
          description: policy.policy_description,
          updated_at: policy.updated_at,
          created_at: policy.created_at,
        };
      }, []),
    [data]
  );

  const handleRowClick: MUIDataTableOptions['onRowClick'] = (
    _rowData,
    { dataIndex }
  ) => {
    const { id } = tableData[dataIndex];
    navigate(`${ROUTES.ADMIN_MANAGE_POLICIES}${id}`);
  };

  const handleCreate = () => {
    navigate(ROUTES.ADMIN_MANAGE_POLICIES_CREATE);
  };

  return (
    <ContentLoader loading={loading}>
      <MuiDataTable
        columns={[
          {
            name: 'name',
            className: 'blue-hover',
            label: formatMessage(messages.name),
            options: {
              searchable: true,
              sort: true,
            },
          },
          {
            name: 'description',
            className: 'small',
            label: formatMessage(messages.description),
            options: {
              searchable: true,
              sort: true,
            },
          },
          {
            name: 'created_at',
            className: 'date',
            label: formatMessage(messages.createdAt),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['created_at']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
          },
          {
            name: 'updated_at',
            className: 'date',
            label: formatMessage(messages.updatedAt),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['updated_at']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
          },
        ]}
        data={tableData}
        options={{
          search: true,
          sort: true,
          filter: true,
          sortOrder: {
            name: 'name',
            direction: 'asc',
          },
          onRowClick: handleRowClick,
          textLabels: {
            body: {
              noMatch: formatMessage(messages.noData),
            },
          },
          customToolbar: () => (
            <RoundedButton
              autoSize
              iconName="plus"
              margin={0}
              onClick={handleCreate}
            >
              {formatMessage(messages.create)}
            </RoundedButton>
          ),
        }}
      />
    </ContentLoader>
  );
}

export default AdminPoliciesList;
